/**
 * Guarda de plano de `/app/*` (spec 10 R4; regra de plano desde a r7-25).
 *
 * Duas decisoes, ambas lidas do servidor:
 *
 * 1. Sem plano vinculado — `user.plan_access.has_access === false` no `/auth/me`, ou um
 *    402 `subscription_required` ja recebido de qualquer rota — vai para a tela
 *    "sem plano" (`NO_PLAN_PATH`), sem deslogar. Configuracoes continua aberta.
 * 2. Com plano, mas a rota pertence a uma feature que o plano nao libera: a pagina e
 *    trocada por um aviso neutro, de `LOCKED_FEATURE_COPY` (a API responderia 403
 *    `feature_not_in_plan` de qualquer jeito).
 *
 * As tabelas (rotas abertas, rota -> feature) moram em `lib/planFeatures.ts`.
 */
import type { ReactNode } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useSubscriptionBlocked } from '@/lib/subscriptionBlock'
import { NO_PLAN_PATH, missingRouteFeature, shouldRedirectToNoPlan } from '@/lib/planFeatures'
import { LOCKED_FEATURE_COPY } from '@/lib/copy/lockedFeature'
import { useAuthStore } from '@/stores/authStore'
import { navItems } from './navItems'

interface SubscriptionGateProps {
  children: ReactNode
}

export function SubscriptionGate({ children }: SubscriptionGateProps) {
  const blocked = useSubscriptionBlocked()
  const user = useAuthStore((state) => state.user)
  const { pathname } = useLocation()

  if (shouldRedirectToNoPlan(user, blocked, pathname)) {
    return <Navigate to={NO_PLAN_PATH} replace />
  }

  const missing = missingRouteFeature(user, pathname)
  if (missing) {
    const label = navItems.find((item) => item.feature === missing)?.label
    return <FeatureNotInPlanNotice label={label} />
  }

  return <>{children}</>
}

function FeatureNotInPlanNotice({ label }: { label?: string }) {
  return (
    <div
      role="status"
      className="mx-auto mt-8 max-w-lg rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
    >
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/20">
        <Lock className="h-6 w-6 text-amber-600 dark:text-amber-400" aria-hidden="true" />
      </div>
      <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">
        {LOCKED_FEATURE_COPY.noticeTitle(label)}
      </h1>
      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
        {LOCKED_FEATURE_COPY.noticeBody}
      </p>
      <Link
        to="/app"
        className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
      >
        Voltar ao início
      </Link>
    </div>
  )
}
